import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, KeyboardEvent } from "react";

import { clamp } from "../../lib/pane-size";
import { cn } from "./cn";

/**
 * The divider between two panes, dragged to move it.
 *
 * A `role="separator"` with a value rather than a bare div with a mousedown,
 * because a divider is one of the few widgets ARIA has an exact answer for and
 * the keyboard half comes almost free: arrows nudge, shift-arrow moves in
 * bigger steps, Home puts it back. Somebody who cannot drag can still make the
 * reply box taller, which is the whole point of the feature for the person most
 * likely to want it.
 *
 * Pointer events, not mouse events, so a trackpad, a pen and a touchscreen all
 * work; and pointer *capture*, so a fast drag that outruns the cursor keeps
 * resizing instead of stopping the moment the pointer leaves the six-pixel
 * gutter. The document's selection is suppressed for the duration -- without
 * that, dragging left across a thread selects the conversation behind it.
 */
export function ResizeHandle({
  axis,
  label,
  size,
  min,
  max,
  invert = false,
  onChange,
  onReset,
  className,
}: {
  /** `x` moves a vertical divider between columns; `y` a horizontal one. */
  axis: "x" | "y";
  /** What is being resized, read out by a screen reader. */
  label: string;
  size: number;
  min: number;
  max: number;
  /**
   * The pane grows when the pointer moves *towards* the axis origin.
   *
   * True for a handle on the leading edge of the thing it sizes -- the details
   * column, whose divider is to its left, and the composer, whose grip is above
   * it. Without this those two resize backwards, which reads as a bug rather
   * than as an inverted sign.
   */
  invert?: boolean;
  onChange: (next: number) => void;
  onReset: () => void;
  className?: string;
}) {
  const [dragging, setDragging] = useState(false);
  const from = useRef<{ at: number; size: number } | null>(null);

  const coordinate = (event: { clientX: number; clientY: number }): number =>
    axis === "x" ? event.clientX : event.clientY;

  /*
   * Suppress the document's selection for the duration of the drag -- without
   * it, dragging left across a thread selects the conversation behind it.
   *
   * An effect keyed on `dragging`, and emphatically not a pair of imperative
   * writes in the pointer handlers, which is what this was. `document.body` is
   * global state and the handlers only cleared it on the one exit they could
   * see: a pointerup on this element. A drag that ended any other way -- the
   * handle unmounting because the pane it divides went away, the pointer being
   * taken by the browser, capture lost -- left `user-select: none` on the body
   * for the rest of the session, and nothing anywhere in the app could be
   * selected or copied until a reload. React's cleanup is the guarantee the
   * handlers could not make: it runs when the drag stops *and* when the
   * component goes, whichever happens.
   */
  useEffect(() => {
    if (!dragging) return;

    const previous = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = previous;
    };
  }, [dragging]);

  const start = (event: ReactPointerEvent<HTMLDivElement>): void => {
    /* Stops the drag from also starting a text selection or, inside the
       composer, from taking focus off the writing surface. */
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    from.current = { at: coordinate(event), size };
    setDragging(true);
  };

  const move = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const origin = from.current;
    if (!origin) return;
    const travelled = (coordinate(event) - origin.at) * (invert ? -1 : 1);
    onChange(clamp(origin.size + travelled, min, max));
  };

  const end = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (!from.current) return;
    from.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = event.shiftKey ? 48 : 12;
    const smaller = axis === "x" ? "ArrowLeft" : "ArrowUp";
    const bigger = axis === "x" ? "ArrowRight" : "ArrowDown";
    const way = invert ? -1 : 1;

    if (event.key === smaller) onChange(clamp(size - step * way, min, max));
    else if (event.key === bigger) onChange(clamp(size + step * way, min, max));
    else if (event.key === "Home") onReset();
    else return;

    event.preventDefault();
  };

  return (
    <div
      role="separator"
      /*
       * The orientation of the *divider*, not of the axis it travels along:
       * a column divider is a vertical line, and calling it horizontal because
       * it moves horizontally is the classic way round to get this wrong.
       */
      aria-orientation={axis === "x" ? "vertical" : "horizontal"}
      aria-label={label}
      aria-valuenow={Math.round(size)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      /* Capture can be taken away -- a context menu, the window losing focus,
         the browser deciding the gesture is a scroll. Without this the drag
         stayed "running" with no pointer behind it and no event coming to end
         it. */
      onLostPointerCapture={end}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      title={`Drag to resize ${label.toLowerCase()} — double-click to reset`}
      className={cn(
        "group relative shrink-0 touch-none",
        axis === "x" ? "w-1.5 cursor-col-resize" : "h-2 w-full cursor-row-resize",
        "focus:outline-none",
        className,
      )}
    >
      {/*
        The line the handle replaces. A column divider draws the full border it
        stands in for; a horizontal one draws a short grip instead, because a
        full-width rule across the middle of the composer reads as the top of a
        new box rather than as something to pull.
      */}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute rounded-full transition-colors",
          axis === "x"
            ? "inset-y-0 left-1/2 w-px -translate-x-1/2"
            : "left-1/2 top-1/2 h-0.5 w-8 -translate-x-1/2 -translate-y-1/2",
          dragging
            ? "bg-brand-500"
            : "bg-line group-hover:bg-brand-500/70 group-focus-visible:bg-brand-500",
        )}
      />
    </div>
  );
}
